import { Icon, Position } from '@blueprintjs/core';
import * as React from 'react';
import { ItemSelector, MultiItemSelector } from 'vet-bones/components';
import { emptyArray } from 'vet-bones/constants';
import { SelectItem } from 'vet-bones/utils/select';

import { LabeledCard } from 'src/components/cards/LabeledCard';
import { TEST_IDS, getModelItemTestId } from 'src/constants/testIds';
import { isConnectedSelector } from 'src/selectors/isConnected';
import { isDatabaseRefreshedSelector } from 'src/selectors/isRefreshingDatabase';
import { modelItemsSelector } from 'src/selectors/modelItems';
import * as copy from 'src/templates/copy';
import { icons } from 'src/templates/icons';
import { colors } from 'src/templates/styles/colors';
import { stopPropagation } from 'src/utils/handlers/stopPropagation';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { stringToSelectItem } from 'src/utils/select/stringToSelectItem';

type ModelCardProps = {
  databaseId: string | null;
  disabled?: boolean;
  models: string[];
  setModels: (models: string[]) => void;
};

export const ModelCard: React.VFC<ModelCardProps> = ({
  databaseId,
  disabled = false,
  models,
  setModels,
}) => {
  const isConnected = useReduxSelector(isConnectedSelector);
  const isDatabaseRefreshed = useReduxSelector(
    isDatabaseRefreshedSelector(databaseId)
  );
  const didDatabaseChange = useDidValueChange(databaseId);
  const didIsConnectedChange = useDidValueChange(isConnected);
  const modelCardDisabled =
    disabled || !isConnected || !databaseId || !isDatabaseRefreshed;
  const modelSelectorRef = React.useRef<MultiItemSelector>(null);

  const modelItems = useReduxSelector(modelItemsSelector(databaseId || ''));

  const selectedModels = React.useMemo(
    () => models.map((model) => stringToSelectItem(model)),
    [models]
  );

  const handleSelectModels = (
    items: SelectItem[],
    evt?: React.SyntheticEvent
  ) => {
    stopPropagation(evt);
    setModels(items.map((item) => item.value));
  };

  React.useEffect(() => {
    if (didDatabaseChange || (didIsConnectedChange && !isConnected)) {
      setModels(emptyArray as any);
    }
  }, [didDatabaseChange, didIsConnectedChange, isConnected, setModels]);

  let cardContent: JSX.Element;
  if (databaseId) {
    cardContent = (
      <ItemSelector.Multi
        buttonIcon={icons.model}
        buttonIconColor={models.length ? colors.darkIcon : undefined}
        buttonText={
          copy.components.cards.model.selected(models) ||
          copy.components.cards.model.placeholder
        }
        clearable
        disabled={disabled}
        filterInputProps={{
          placeholder: copy.components.defaults.filter,
        }}
        items={modelItems}
        itemTestId={getModelItemTestId}
        onButtonClick={stopPropagation}
        onClear={stopPropagation}
        onSelect={handleSelectModels}
        popoverProps={{
          backdropProps: { onClick: stopPropagation },
          position: Position.TOP,
        }}
        selected={selectedModels}
        selectorRef={modelSelectorRef}
        testId={TEST_IDS.model.selector}
      />
    );
  } else {
    cardContent = <Icon icon={icons.model} />;
  }
  return (
    <LabeledCard
      disabled={modelCardDisabled}
      label={copy.components.cards.model.label}
      labelClassName="sd-model-card"
      onClick={() => {
        if (!modelCardDisabled) {
          modelSelectorRef.current?.setState?.({ isOpen: true });
        }
      }}
      testId={TEST_IDS.model.card}
    >
      {cardContent}
    </LabeledCard>
  );
};
